import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Search, ShoppingCart, Eye, FileText, Download, Edit, RotateCcw } from 'lucide-react';

const PurchaseEntry = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  
  const [purchases, setPurchases] = useState([]);
  const [deletedPurchases, setDeletedPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Filter states
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    purchase_date: new Date().toISOString().split('T')[0],
    supplier_id: '',
    po_number: '',
    invoice_number: '',
    dc_number: '',
    notes: '',
    discount_percentage: 0,
    round_off: 0,
    payment_status: 'Pending',
    payment_mode: '',
    reference_number: ''
  });

  // Line items
  const [lineItems, setLineItems] = useState([]);

  const fetchPurchases = useCallback(async () => {
    try {
      let url = '/purchases/';
      const params = new URLSearchParams();
      if (filterSupplier) params.append('supplier_id', filterSupplier);
      if (filterStatus) params.append('payment_status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      setPurchases(response.data);
      
      // Fetch deleted purchases for admin
      if (isAdmin) {
        try {
          const deletedRes = await api.get('/purchases/deleted');
          setDeletedPurchases(deletedRes.data);
        } catch (err) {
          console.error('Failed to fetch deleted purchases:', err);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch purchases');
    } finally {
      setLoading(false);
    }
  }, [filterSupplier, filterStatus, isAdmin]);

  const fetchMasterData = useCallback(async () => {
    try {
      const [suppliersRes, productsRes] = await Promise.all([
        api.get('/suppliers/'),
        api.get('/products/')
      ]);
      setSuppliers(suppliersRes.data.filter(s => s.status === 'Active'));
      setProducts(productsRes.data.filter(p => p.status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch master data');
    }
  }, []);

  useEffect(() => {
    fetchPurchases();
    fetchMasterData();
  }, [fetchPurchases, fetchMasterData]);

  const handleOpenDialog = (purchase = null) => {
    if (purchase) {
      // Edit mode
      setEditMode(true);
      setSelectedPurchase(purchase);
      setFormData({
        purchase_date: purchase.purchase_date || new Date().toISOString().split('T')[0],
        supplier_id: purchase.supplier_id || '',
        po_number: purchase.po_number || '',
        invoice_number: purchase.invoice_number || '',
        dc_number: purchase.dc_number || '',
        notes: purchase.notes || '',
        discount_percentage: purchase.discount_percentage || 0,
        round_off: purchase.round_off || 0,
        payment_status: purchase.payment_status || 'Pending',
        payment_mode: purchase.payment_mode || '',
        reference_number: purchase.reference_number || ''
      });
      // Map items to line items format
      const items = (purchase.items || []).map(item => ({
        product_id: item.product_id || '',
        serial_number: item.serial_number || '',
        serial_numbers: item.serial_numbers || [],
        qty: item.qty || 1,
        purchase_price: item.purchase_price || 0,
        tax_percentage: item.tax_percentage || 0,
        discount_percentage: item.discount_percentage || 0,
        requires_serial: item.requires_serial || false
      }));
      setLineItems(items.length > 0 ? items : [{
        product_id: '',
        serial_number: '',
        serial_numbers: [],
        qty: 1,
        purchase_price: 0,
        tax_percentage: 0,
        discount_percentage: 0,
        requires_serial: false
      }]);
    } else {
      // Create mode
      setEditMode(false);
      setSelectedPurchase(null);
      setFormData({
        purchase_date: new Date().toISOString().split('T')[0],
        supplier_id: '',
        po_number: '',
        invoice_number: '',
        dc_number: '',
        notes: '',
        discount_percentage: 0,
        round_off: 0,
        payment_status: 'Pending',
        payment_mode: '',
        reference_number: ''
      });
      setLineItems([{
        product_id: '',
        serial_number: '',
        serial_numbers: [],
        qty: 1,
        purchase_price: 0,
        tax_percentage: 0,
        discount_percentage: 0,
        requires_serial: false
      }]);
    }
    setDialogOpen(true);
  };

  const handleEditPurchase = async (purchase) => {
    try {
      const response = await api.get(`/purchases/${purchase.id}`);
      handleOpenDialog(response.data);
    } catch (error) {
      toast.error('Failed to load purchase details for editing');
    }
  };

  const handleViewPurchase = async (purchase) => {
    try {
      const response = await api.get(`/purchases/${purchase.id}`);
      setSelectedPurchase(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load purchase details');
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      product_id: '',
      serial_number: '',
      serial_numbers: [],
      qty: 1,
      purchase_price: 0,
      tax_percentage: 0,
      discount_percentage: 0,
      requires_serial: false
    }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  // Check if product requires serial numbers based on category
  const checkSerialRequired = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product?.category_id) return false;
    
    try {
      const response = await api.get(`/categories/${product.category_id}/requires-serial`);
      return response.data.requires_serial;
    } catch (error) {
      return false;
    }
  };

  const updateLineItem = async (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // If product selected, auto-fill tax percentage and check serial requirement
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === value);
      if (product?.tax_group_percentage) {
        updated[index].tax_percentage = product.tax_group_percentage;
      }
      // Check if serial number is required
      const requiresSerial = await checkSerialRequired(value);
      updated[index].requires_serial = requiresSerial;
      // Initialize serial_numbers array based on qty
      if (requiresSerial) {
        updated[index].serial_numbers = Array(Math.max(1, parseInt(updated[index].qty) || 1)).fill('');
      }
    }
    
    // If qty changed and serial required, adjust serial_numbers array
    if (field === 'qty' && updated[index].requires_serial) {
      const newQty = parseInt(value) || 1;
      const currentSerials = updated[index].serial_numbers || [];
      if (newQty > currentSerials.length) {
        updated[index].serial_numbers = [...currentSerials, ...Array(newQty - currentSerials.length).fill('')];
      } else {
        updated[index].serial_numbers = currentSerials.slice(0, newQty);
      }
    }
    
    setLineItems(updated);
  };

  const updateSerialNumber = (lineIndex, serialIndex, value) => {
    const updated = [...lineItems];
    updated[lineIndex].serial_numbers[serialIndex] = value;
    setLineItems(updated);
  };

  const calculateLineItemTotal = (item) => {
    const subtotal = item.qty * item.purchase_price;
    const discount = subtotal * (item.discount_percentage / 100);
    const taxable = subtotal - discount;
    const tax = taxable * (item.tax_percentage / 100);
    return {
      subtotal,
      discount,
      tax,
      total: taxable + tax
    };
  };

  const calculateTotals = () => {
    let totalAmount = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    lineItems.forEach(item => {
      const calc = calculateLineItemTotal(item);
      totalAmount += calc.subtotal;
      totalTax += calc.tax;
      totalDiscount += calc.discount;
    });

    const overallDiscount = totalAmount * (formData.discount_percentage / 100);
    const subtotalBeforeRound = totalAmount - totalDiscount - overallDiscount + totalTax;
    const roundOff = parseFloat(formData.round_off) || 0;
    const netAmount = subtotalBeforeRound + roundOff;

    return {
      totalAmount: totalAmount.toFixed(2),
      totalTax: totalTax.toFixed(2),
      totalDiscount: (totalDiscount + overallDiscount).toFixed(2),
      subtotalBeforeRound: subtotalBeforeRound.toFixed(2),
      roundOff: roundOff.toFixed(2),
      netAmount: netAmount.toFixed(2)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.invoice_number) {
      toast.error('Invoice number is required');
      return;
    }
    if (lineItems.some(item => !item.product_id || item.qty <= 0)) {
      toast.error('Please fill all product details');
      return;
    }
    if (formData.payment_status === 'Paid' && !formData.payment_mode) {
      toast.error('Please select payment mode');
      return;
    }
    
    // Validate serial numbers for required items
    for (const item of lineItems) {
      if (item.requires_serial) {
        const qty = parseInt(item.qty) || 0;
        const serials = (item.serial_numbers || []).filter(sn => sn && sn.trim() !== '');
        if (serials.length !== qty) {
          const product = products.find(p => p.id === item.product_id);
          toast.error(`Please enter all ${qty} serial numbers for ${product?.name || 'product'}`);
          return;
        }
        // Check for duplicate serial numbers
        const uniqueSerials = new Set(serials.map(sn => sn.trim().toUpperCase()));
        if (uniqueSerials.size !== serials.length) {
          toast.error('Duplicate serial numbers found. Each serial number must be unique.');
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        items: lineItems.map(item => ({
          product_id: item.product_id,
          serial_number: (item.serial_number || '').slice(0, 24),
          serial_numbers: (item.serial_numbers || []).filter(sn => sn && sn.trim() !== '').map(sn => sn.trim().toUpperCase().slice(0, 24)),
          qty: parseFloat(item.qty),
          purchase_price: parseFloat(item.purchase_price),
          tax_percentage: parseFloat(item.tax_percentage || 0),
          discount_percentage: parseFloat(item.discount_percentage || 0)
        }))
      };

      if (editMode && selectedPurchase) {
        // Update existing purchase
        await api.put(`/purchases/${selectedPurchase.id}`, payload);
        toast.success('Purchase updated successfully');
      } else {
        // Create new purchase
        await api.post('/purchases/', payload);
        toast.success('Purchase entry created successfully');
      }
      setDialogOpen(false);
      setEditMode(false);
      setSelectedPurchase(null);
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} purchase`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = async (purchase) => {
    if (!window.confirm('Restore this purchase? This will re-add inventory quantities.')) return;

    try {
      await api.post(`/purchases/${purchase.id}/restore`);
      toast.success('Purchase restored successfully');
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore purchase');
    }
  };

  const handleDelete = async (purchase) => {
    if (!window.confirm('Delete this purchase? This will reverse inventory quantities.')) return;

    try {
      await api.delete(`/purchases/${purchase.id}`);
      toast.success('Purchase deleted successfully');
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete purchase');
    }
  };

  const totals = calculateTotals();

  const filteredPurchases = purchases.filter(purchase =>
    purchase.purchase_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    purchase.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    purchase.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Entry</h1>
          <p className="text-gray-600">Manage purchase entries and stock inward</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          New Purchase
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search purchases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterSupplier || 'all'} onValueChange={(v) => setFilterSupplier(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Purchases Table with Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Purchases
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Purchases ({filteredPurchases.length})</TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="deleted" className="text-red-600">
                  Deleted ({deletedPurchases.length})
                </TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="all">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Purchase #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPurchases.length > 0 ? (
                    filteredPurchases.map((purchase) => (
                      <TableRow key={purchase.id}>
                        <TableCell className="font-mono text-sm">{purchase.purchase_number}</TableCell>
                        <TableCell>{new Date(purchase.purchase_date).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell>{purchase.supplier_name}</TableCell>
                        <TableCell>{purchase.invoice_number}</TableCell>
                        <TableCell className="text-right font-medium">₹{purchase.net_amount?.toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          <Badge variant={purchase.payment_status === 'Paid' ? 'default' : 'destructive'}>
                            {purchase.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleViewPurchase(purchase)} title="View">
                            <Eye className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => handleEditPurchase(purchase)} title="Edit">
                              <Edit className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(purchase)} title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                        No purchases found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TabsContent>
            
            {isAdmin && (
              <TabsContent value="deleted">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Purchase #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Deleted At</TableHead>
                      <TableHead>Deleted By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletedPurchases.length > 0 ? (
                      deletedPurchases.map((purchase) => (
                        <TableRow key={purchase.id} className="bg-red-50">
                          <TableCell className="font-mono text-sm">{purchase.purchase_number}</TableCell>
                          <TableCell>{new Date(purchase.purchase_date).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell>{purchase.supplier_name}</TableCell>
                          <TableCell>{purchase.invoice_number}</TableCell>
                          <TableCell className="text-right font-medium">₹{purchase.net_amount?.toLocaleString('en-IN')}</TableCell>
                          <TableCell className="text-red-600 text-sm">
                            {purchase.deleted_at ? new Date(purchase.deleted_at).toLocaleString('en-IN') : '-'}
                          </TableCell>
                          <TableCell>{purchase.deleted_by_name || '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleViewPurchase(purchase)} title="View">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-green-600" 
                              onClick={() => handleRestore(purchase)}
                              title="Restore"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                          No deleted purchases
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* New/Edit Purchase Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setEditMode(false);
          setSelectedPurchase(null);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Purchase Entry' : 'New Purchase Entry'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Header Fields */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Purchase Date *</Label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Supplier *</Label>
                  <Select
                    value={formData.supplier_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, supplier_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select Supplier</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Invoice Number *</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="INV-001"
                  />
                </div>
                <div>
                  <Label>PO Number</Label>
                  <Input
                    value={formData.po_number}
                    onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>DC Number</Label>
                  <Input
                    value={formData.dc_number}
                    onChange={(e) => setFormData({ ...formData, dc_number: e.target.value })}
                    placeholder="Delivery Challan Number"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base font-medium">Products</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="h-4 w-4 mr-1" /> Add Product
                  </Button>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[250px]">Product</TableHead>
                      <TableHead className="w-[80px]">Qty</TableHead>
                      <TableHead className="w-[100px]">Price</TableHead>
                      <TableHead className="w-[80px]">Tax %</TableHead>
                      <TableHead className="w-[80px]">Disc %</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item, index) => {
                      const calc = calculateLineItemTotal(item);
                      const selectedProduct = products.find(p => p.id === item.product_id);
                      return (
                        <React.Fragment key={index}>
                          <TableRow>
                            <TableCell>
                              <Select
                                value={item.product_id || 'none'}
                                onValueChange={(v) => updateLineItem(index, 'product_id', v === 'none' ? '' : v)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Product" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select Product</SelectItem>
                                  {products.map(p => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.name} {p.model ? `(${p.model})` : ''} - {p.brand_name || 'No Brand'}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {item.requires_serial && (
                                <Badge className="mt-1 bg-amber-100 text-amber-800 text-xs">Serial Required</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                value={item.qty}
                                onChange={(e) => updateLineItem(index, 'qty', parseFloat(e.target.value) || 0)}
                                className="w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.purchase_price}
                                onChange={(e) => updateLineItem(index, 'purchase_price', parseFloat(e.target.value) || 0)}
                                className="w-24"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={item.tax_percentage}
                                onChange={(e) => updateLineItem(index, 'tax_percentage', parseFloat(e.target.value) || 0)}
                                className="w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={item.discount_percentage}
                                onChange={(e) => updateLineItem(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                                className="w-20"
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              ₹{calc.total.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {lineItems.length > 1 && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                          {/* Serial Numbers Row - Only show if required and qty > 0 */}
                          {item.requires_serial && item.qty > 0 && (
                            <TableRow className="bg-amber-50">
                              <TableCell colSpan={7} className="py-2">
                                <div className="flex flex-wrap gap-2 items-center">
                                  <span className="text-sm font-medium text-amber-800 mr-2">
                                    Serial Numbers ({item.serial_numbers?.length || 0}/{parseInt(item.qty) || 0}):
                                  </span>
                                  {(item.serial_numbers || []).map((sn, snIndex) => (
                                    <Input
                                      key={snIndex}
                                      value={sn}
                                      onChange={(e) => updateSerialNumber(index, snIndex, e.target.value.slice(0, 24))}
                                      placeholder={`S/N ${snIndex + 1}`}
                                      className="w-32 h-8 text-sm"
                                      maxLength={24}
                                    />
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          {/* Non-required serial - optional single field */}
                          {!item.requires_serial && item.product_id && (
                            <TableRow className="bg-gray-50">
                              <TableCell colSpan={7} className="py-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-600">Serial No. (optional):</span>
                                  <Input
                                    value={item.serial_number || ''}
                                    onChange={(e) => updateLineItem(index, 'serial_number', e.target.value.slice(0, 24))}
                                    placeholder="Optional (max 24 chars)"
                                    className="w-40 h-8 text-sm"
                                    maxLength={24}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-2 gap-4">
                <div></div>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Total Amount:</span>
                      <span>₹{totals.totalAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Tax:</span>
                      <span>₹{totals.totalTax}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Overall Discount %:</span>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.discount_percentage}
                        onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) || 0 })}
                        className="w-20"
                      />
                    </div>
                    <div className="flex justify-between">
                      <span>Total Discount:</span>
                      <span>-₹{totals.totalDiscount}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between items-center">
                      <span>Round Off:</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.round_off}
                        onChange={(e) => setFormData({ ...formData, round_off: parseFloat(e.target.value) || 0 })}
                        className="w-24"
                        data-testid="purchase-round-off"
                        placeholder="+/- 0.00"
                      />
                    </div>
                    <hr />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Net Amount:</span>
                      <span className="text-green-700">₹{totals.netAmount}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Payment Status *</Label>
                  <Select
                    value={formData.payment_status}
                    onValueChange={(v) => setFormData({ ...formData, payment_status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.payment_status === 'Paid' && (
                  <>
                    <div>
                      <Label>Payment Mode *</Label>
                      <Select
                        value={formData.payment_mode || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, payment_mode: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select Mode</SelectItem>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                          <SelectItem value="UPI">UPI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Reference Number</Label>
                      <Input
                        value={formData.reference_number}
                        onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                        placeholder="Transaction ID / Cheque No."
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => {
                setDialogOpen(false);
                setEditMode(false);
                setSelectedPurchase(null);
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Purchase' : 'Create Purchase')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Purchase Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Details - {selectedPurchase?.purchase_number}</DialogTitle>
          </DialogHeader>
          {selectedPurchase && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-gray-500">Purchase Date</Label>
                  <p className="font-medium">{new Date(selectedPurchase.purchase_date).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Supplier</Label>
                  <p className="font-medium">{selectedPurchase.supplier?.name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Invoice Number</Label>
                  <p className="font-medium">{selectedPurchase.invoice_number}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Payment Status</Label>
                  <Badge variant={selectedPurchase.payment_status === 'Paid' ? 'default' : 'destructive'}>
                    {selectedPurchase.payment_status}
                  </Badge>
                </div>
              </div>

              {selectedPurchase.po_number && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">PO Number</Label>
                    <p className="font-medium">{selectedPurchase.po_number}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">DC Number</Label>
                    <p className="font-medium">{selectedPurchase.dc_number || '-'}</p>
                  </div>
                </div>
              )}

              {/* Line Items */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Serial No.</TableHead>
                      <TableHead>HSN</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPurchase.items?.map((item, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell>
                            <div>
                              <span className="font-medium">{item.product_name}</span>
                              {item.product_model && <span className="text-gray-500 ml-1">({item.product_model})</span>}
                              {item.brand_name && <Badge variant="outline" className="ml-2 text-xs">{item.brand_name}</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>{item.serial_number || '-'}</TableCell>
                          <TableCell>{item.hsn_number || '-'}</TableCell>
                          <TableCell className="text-right">{item.qty}</TableCell>
                          <TableCell className="text-right">₹{item.purchase_price?.toLocaleString('en-IN')}</TableCell>
                          <TableCell className="text-right">{item.tax_percentage}%</TableCell>
                          <TableCell className="text-right font-medium">₹{item.total?.toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                        {item.serial_numbers && item.serial_numbers.length > 0 && (
                          <TableRow className="bg-amber-50">
                            <TableCell colSpan={7} className="py-2">
                              <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-sm font-medium text-amber-800 mr-1">Serial Numbers:</span>
                                {item.serial_numbers.map((sn, snIdx) => (
                                  <Badge key={snIdx} variant="outline" className="bg-white text-amber-900 font-mono text-xs">
                                    {typeof sn === 'object' ? sn.serial_number : sn}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  {selectedPurchase.notes && (
                    <div>
                      <Label className="text-gray-500">Notes</Label>
                      <p className="font-medium">{selectedPurchase.notes}</p>
                    </div>
                  )}
                  {selectedPurchase.payment_status === 'Paid' && (
                    <div className="mt-4">
                      <Label className="text-gray-500">Payment Details</Label>
                      <p className="font-medium">
                        {selectedPurchase.payment_mode}
                        {selectedPurchase.reference_number && ` - Ref: ${selectedPurchase.reference_number}`}
                      </p>
                    </div>
                  )}
                </div>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Total Amount:</span>
                      <span>₹{selectedPurchase.total_amount?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Tax:</span>
                      <span>₹{selectedPurchase.total_tax?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Discount:</span>
                      <span>-₹{selectedPurchase.total_discount?.toLocaleString('en-IN')}</span>
                    </div>
                    {(selectedPurchase.round_off !== undefined && selectedPurchase.round_off !== 0) && (
                      <div className="flex justify-between">
                        <span>Round Off:</span>
                        <span>{selectedPurchase.round_off >= 0 ? '+' : ''}₹{selectedPurchase.round_off?.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <hr />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Net Amount:</span>
                      <span className="text-green-700">₹{selectedPurchase.net_amount?.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseEntry;
